import React, { useState } from 'react';
import { Activity } from '../types';
import { StatusBadge } from '../components/StatusBadge';
import { DownloadIcon, FileTextIcon, FilterIcon, BarChart3Icon } from 'lucide-react';
interface ReportPageProps {
  activities: Activity[];
}
export function ReportPage({
  activities
}: ReportPageProps) {
  const [unitFilter, setUnitFilter] = useState('Semua Unit');
  const [statusFilter, setStatusFilter] = useState('Semua Status');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const units = ['Semua Unit', ...Array.from(new Set(activities.map(a => a.unit)))];
  const statuses = ['Semua Status', 'Draft', 'Baru', 'Berjalan', 'Menunggu Verifikasi', 'Tertunda', 'Selesai', 'Dibatalkan'];
  const filteredData = activities.filter(act => {
    const matchUnit = unitFilter === 'Semua Unit' || act.unit === unitFilter;
    const matchStatus = statusFilter === 'Semua Status' || act.status === statusFilter;
    let matchDate = true;
    if (dateFrom && dateTo) {
      matchDate = act.startDate >= dateFrom && act.dueDate <= dateTo;
    } else if (dateFrom) {
      matchDate = act.startDate >= dateFrom;
    } else if (dateTo) {
      matchDate = act.dueDate <= dateTo;
    }
    return matchUnit && matchStatus && matchDate;
  });
  const activeCount = filteredData.filter(a => ['Baru', 'Berjalan', 'Menunggu Verifikasi'].includes(a.status)).length;
  const completedCount = filteredData.filter(a => a.status === 'Selesai').length;
  const delayedCount = filteredData.filter(a => a.status === 'Tertunda').length;
  const today = new Date().toISOString().split('T')[0];
  const overdueCount = filteredData.filter(a => a.dueDate < today && a.status !== 'Selesai').length;
  const inputClasses = 'w-full border border-[#1E3A5F] rounded text-sm py-2 px-3 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0]';
  const labelClasses = 'block text-xs font-semibold text-[#94A3B8] mb-1.5';
  const handleExportPdf = () => alert('Laporan PDF sedang digenerate...');
  const handleExportExcel = () => alert('Laporan Excel sedang digenerate...');
  const handleGenerate = () => alert('Data berhasil diperbarui sesuai filter.');
  return <div className="space-y-6 flex flex-col h-full" data-id="element-970">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-971">
        <h2 className="text-2xl font-bold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-972">
          <BarChart3Icon className="w-6 h-6 mr-3 text-[#00D4FF]" data-id="element-973" />
          LAPORAN KEGIATAN OPERASIONAL
        </h2>
      </div>

      {/* Filters */}
      <div className="bg-[#111827] p-5 rounded shadow-lg border border-[#1E3A5F]" data-id="element-974">
        <div className="flex items-center mb-4 text-[#00D4FF] font-semibold text-sm" data-id="element-975">
          <FilterIcon className="w-4 h-4 mr-2" data-id="element-976" />
          Filter Laporan
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end" data-id="element-977">
          <div data-id="element-978">
            <label className={labelClasses} data-id="element-979">Periode Dari</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={`${inputClasses} font-mono`} data-id="element-980" />
          </div>
          <div data-id="element-981">
            <label className={labelClasses} data-id="element-982">Periode Sampai</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={`${inputClasses} font-mono`} data-id="element-983" />
          </div>
          <div data-id="element-984">
            <label className={labelClasses} data-id="element-985">Unit Pelaksana</label>
            <select value={unitFilter} onChange={e => setUnitFilter(e.target.value)} className={inputClasses} data-id="element-986">
              {units.map(u => <option key={u} data-id="element-987">{u}</option>)}
            </select>
          </div>
          <div data-id="element-988">
            <label className={labelClasses} data-id="element-989">Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={inputClasses} data-id="element-990">
              {statuses.map(s => <option key={s} data-id="element-991">{s}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap justify-end gap-3 pt-5 border-t border-[#1E3A5F]" data-id="element-992">
          <button onClick={handleExportPdf} className="px-4 py-2 border border-[#1E3A5F] text-[#E2E8F0] bg-[#1A2332] rounded hover:bg-[#1E3A5F] text-xs font-bold uppercase flex items-center transition-colors" data-id="element-993">
            <FileTextIcon className="w-4 h-4 mr-2 text-[#00D4FF]" data-id="element-994" /> EKSPOR PDF
          </button>
          <button onClick={handleExportExcel} className="px-4 py-2 border border-[#00FF88]/50 text-[#00FF88] bg-[#00FF88]/10 rounded hover:bg-[#00FF88]/20 text-xs font-bold uppercase flex items-center transition-colors glow-green" data-id="element-995">
            <DownloadIcon className="w-4 h-4 mr-2" data-id="element-996" /> EKSPOR EXCEL
          </button>
          <button onClick={handleGenerate} className="px-4 py-2 bg-[#00D4FF] text-[#0B0F1A] rounded hover:bg-[#00D4FF]/90 text-xs font-bold uppercase transition-colors glow-cyan" data-id="element-997">
            GENERATE DATA
          </button>
        </div>
      </div>

      {/* Summary Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-998">
        <div className="bg-[#111827] p-4 rounded border border-[#1E3A5F] border-t-2 border-t-[#0EA5E9] text-center shadow-lg" data-id="element-999">
          <p className="text-xs text-[#94A3B8] uppercase font-semibold" data-id="element-1000">
            Total Aktif
          </p>
          <p className="text-2xl font-mono font-bold text-[#0EA5E9] mt-1" data-id="element-1001">
            {activeCount}
          </p>
        </div>
        <div className="bg-[#111827] p-4 rounded border border-[#1E3A5F] border-t-2 border-t-[#00FF88] text-center shadow-lg" data-id="element-1002">
          <p className="text-xs text-[#94A3B8] uppercase font-semibold" data-id="element-1003">
            Total Selesai
          </p>
          <p className="text-2xl font-mono font-bold text-[#00FF88] mt-1" data-id="element-1004">
            {completedCount}
          </p>
        </div>
        <div className="bg-[#111827] p-4 rounded border border-[#1E3A5F] border-t-2 border-t-[#FFB800] text-center shadow-lg" data-id="element-1005">
          <p className="text-xs text-[#94A3B8] uppercase font-semibold" data-id="element-1006">
            Total Tertunda
          </p>
          <p className="text-2xl font-mono font-bold text-[#FFB800] mt-1" data-id="element-1007">
            {delayedCount}
          </p>
        </div>
        <div className="bg-[#111827] p-4 rounded border border-[#1E3A5F] border-t-2 border-t-[#FF3B3B] text-center shadow-lg" data-id="element-1008">
          <p className="text-xs text-[#94A3B8] uppercase font-semibold" data-id="element-1009">
            Total Overdue
          </p>
          <p className="text-2xl font-mono font-bold text-[#FF3B3B] mt-1" data-id="element-1010">
            {overdueCount}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] overflow-hidden flex-1" data-id="element-1011">
        <div className="overflow-x-auto" data-id="element-1012">
          <table className="w-full text-sm text-left" data-id="element-1013">
            <thead className="text-xs text-[#94A3B8] uppercase bg-[#0F1629] border-b border-[#1E3A5F]" data-id="element-1014">
              <tr data-id="element-1015">
                <th className="px-4 py-3 font-semibold w-12 text-center" data-id="element-1016">NO</th>
                <th className="px-4 py-3 font-semibold" data-id="element-1017">KEGIATAN</th>
                <th className="px-4 py-3 font-semibold" data-id="element-1018">PIC</th>
                <th className="px-4 py-3 font-semibold" data-id="element-1019">UNIT</th>
                <th className="px-4 py-3 font-semibold" data-id="element-1020">STATUS</th>
                <th className="px-4 py-3 font-semibold" data-id="element-1021">PROGRES</th>
                <th className="px-4 py-3 font-semibold" data-id="element-1022">DEADLINE</th>
              </tr>
            </thead>
            <tbody data-id="element-1023">
              {filteredData.length > 0 ? filteredData.map((act, index) => <tr key={act.id} className="border-b border-[#1E3A5F]/50 hover:bg-[#1A2332] transition-colors" data-id="element-1024">
                    <td className="px-4 py-3 text-center text-[#94A3B8] font-mono text-xs" data-id="element-1025">
                      {(index + 1).toString().padStart(2, '0')}
                    </td>
                    <td className="px-4 py-3 font-medium text-[#E2E8F0] max-w-[250px] truncate text-sm" title={act.title} data-id="element-1026">
                      {act.title}
                    </td>
                    <td className="px-4 py-3 text-[#94A3B8] text-sm" data-id="element-1027">
                      {act.personInCharge}
                    </td>
                    <td className="px-4 py-3 text-[#94A3B8] text-sm" data-id="element-1028">
                      {act.unit}
                    </td>
                    <td className="px-4 py-3" data-id="element-1029">
                      <StatusBadge status={act.status} data-id="element-1030" />
                    </td>
                    <td className="px-4 py-3 font-mono text-[#00D4FF] text-xs" data-id="element-1031">
                      {act.progressPercent}%
                    </td>
                    <td className="px-4 py-3 text-[#E2E8F0] font-mono text-xs" data-id="element-1032">
                      {act.dueDate}
                    </td>
                  </tr>) : <tr data-id="element-1033">
                  <td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8] text-sm" data-id="element-1034">
                    Tidak ada data laporan yang sesuai dengan filter.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}